import { AxiosResponse } from 'axios';
import { v4 as uuid } from 'uuid';

import { Component, inject } from '@angular/core';

import { EmailUtils, MailTemplateItem, withModule } from '@skylab-shared/business';
import { EventBusBase, L10nBase, LocalContext, ObservableCollection, RootContextBase, buildValidationKey } from '@skylab-shared/core';
import { ToastDelay } from '@skylab-shared/ui';

import { DialogButtonResult, DialogContentComponentBase, DialogContentViewModel, DialogInsService, DialogState, ToastService } from '@skylab-angular-shared/ui';
import { head, sameValues } from '@skylab-shared/utils';
import { BehaviorSubject } from 'rxjs';
import { ViewValidation, ViewValidationService } from '../../form/form-validation';
import { ContentLengthErrorValidator, EmptyEmailErrorValidator, IEmailValidation, NotValidEmailErrorValidator, SenderNameLengthErrorValidator, SubjectLengthErrorValidator } from './send-email-dialog-validator';

export const SenderNamePlaceholder = 'Subiekt 123';

export interface SendEmailDialogViewModelBase extends DialogContentViewModel {
    sun: string;
    validationSource: { validationResult: ObservableCollection<IEmailValidation> };
    email: { value: string };
    mailTemplate: {
        value: MailTemplateItem | null
    };
    mailTemplateSubject: {
        value: string
    };
    mailTemplateContent: {
        value: string
    };
    senderName: {
        value: string
    };
    senderNameValue: string;
    senderNamePlaceholder: string;
    mailTemplateList: MailTemplateItem[];
    isReady: boolean;
    showValidations: BehaviorSubject<boolean>,
    commonAreaId: string;
    messageAreaId: string;
    optionsAreaId: string;
    isEditingTemplateActive: boolean;
}

@Component({
    template: '',
})
export abstract class SendEmailDialogComponentBase<TEmailDocumentDialogViewModel extends SendEmailDialogViewModelBase, TDialogData>
    extends DialogContentComponentBase<TEmailDocumentDialogViewModel, TDialogData> {

    public isSending!: boolean;
    protected localContext: LocalContext;

    private viewValidationService = inject(ViewValidationService);
    public viewValidation!: ViewValidation;
    public isFormDirty = new BehaviorSubject(false);

    public constructor(
        rootContext: RootContextBase,
        dialogService: DialogInsService,
        protected readonly toastService: ToastService,
        protected readonly l10n: L10nBase,
        protected readonly eventBus: EventBusBase
    ) {
        super(dialogService);
        this.localContext = rootContext.createLocalContext();
    }

    public override ngOnInit(): void {
        super.ngOnInit();
        this.isFormDirty.subscribe(e => {
            if (e) this.viewModel.showValidations.next(true);
        });
    }

    public override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.viewValidation?.dispose();
    }

    public override onAction(data: DialogButtonResult): void {
        if (data.state === DialogState.Confirm) {
            this.sendEmailAsync();
        }
        if (data.state === DialogState.Decline ||
            data.state === DialogState.Cancel) {
            this.onCancel();
        }
    }

    protected override getInitialState(): TEmailDocumentDialogViewModel {
        const self = this;
        let emails: string[] = [];
        let subjectValue: string = '';
        let contentValue: string = '';
        let internalMailTemplateItem: MailTemplateItem | null = null;

        const viewModel = <TEmailDocumentDialogViewModel>{
            sun: 'send-email-dialog-context',
            validationSource: { validationResult: new ObservableCollection<IEmailValidation>() },
            email: {
                get value(): string {
                    return EmailUtils.concatEmails(emails) || '';
                },
                set value(val: string) {
                    emails = EmailUtils.extractEmails(val);
                    self.validateViewModel();
                }
            },
            mailTemplateSubject: {
                get value(): string {
                    return subjectValue;
                },
                set value(val: string) {
                    subjectValue = val;
                    self.validateViewModel();
                }
            },
            mailTemplateContent: {
                get value(): string {
                    return contentValue;
                },
                set value(val: string) {
                    contentValue = val;
                    self.validateViewModel();
                }
            },
            mailTemplate: {
                get value(): MailTemplateItem | null {
                    return internalMailTemplateItem;
                },
                set value(value: MailTemplateItem | null) {
                    internalMailTemplateItem = value;
                    if (value) {
                        self.viewModel.mailTemplateSubject.value = value.subjectPattern;
                        self.viewModel.mailTemplateContent.value = value.messagePattern;
                        self.viewModel.senderNameValue = value.senderName;
                    }
                },
            },
            senderName: {
                get value(): string {
                    return self.viewModel.senderNameValue || SenderNamePlaceholder;
                },
                set value(value: string) {
                    self.viewModel.senderNameValue = value;
                    self.validateViewModel();
                }
            },
            senderNameValue: '',
            senderNamePlaceholder: SenderNamePlaceholder,
            isReady: false,
            showValidations: new BehaviorSubject(false),
            commonAreaId: uuid(),
            messageAreaId: uuid(),
            optionsAreaId: uuid(),
            isEditingTemplateActive: withModule('MailTemplateEditing')
        };

        this.viewValidation = this.viewValidationService.create([viewModel.validationSource], viewModel.showValidations);

        return viewModel;
    }

    protected override isReady(): void {
        this.viewModel.isReady = true;
    }

    public getValidationKey(propertyName: string): string {
        return buildValidationKey(this.viewModel.sun, propertyName);
    }

    private validateViewModel(): IEmailValidation[] {
        if (!this.viewModel?.isReady) return [];

        const errors = [
            new SubjectLengthErrorValidator().execute('mailTemplateSubject', this.viewModel.mailTemplateSubject.value),
            new ContentLengthErrorValidator().execute('mailTemplateContent', this.viewModel.mailTemplateContent.value),
            new SenderNameLengthErrorValidator().execute('senderName', this.viewModel.senderNameValue)
        ];

        if (this.validEmail) {
            errors.push(...[
                new EmptyEmailErrorValidator().execute('email', this.viewModel.email.value),
                new NotValidEmailErrorValidator().execute('email', this.viewModel.email.value),
            ]);
        }

        this.viewModel.validationSource.validationResult.clear();
        const violations = errors.filter(error => error != null);
        violations.forEach(elem => this.viewModel.validationSource.validationResult.push(elem));
        return violations;
    }

    protected get isModelValid(): boolean {
        return !(this.validateViewModel().length > 0);
    }

    protected get validEmail(): boolean {
        return true;
    }

    protected onSendEmailError(err: AxiosResponse): void {
        if (err.status === 429) {
            this.toastService.showAsync({
                type: 'warning',
                text: this.getOnSendEmailLimitReachedErrorMessage(err),
                duration: ToastDelay.Regular,
            });
        } else {
            this.toastService.showAsync({
                type: 'error',
                text: this.getOnSendEmailErrorMessage(err),
                duration: ToastDelay.Regular,
            });
        }

        this.dialogService.close({ state: DialogState.Cancel });
    }

    protected getOnSendEmailErrorMessage(_: AxiosResponse): string {
        return this.l10n._T('scenes.shared.dialog.send-email.messages.email-error');
    }

    protected getOnSendEmailLimitReachedErrorMessage(_: AxiosResponse): string {
        return this.l10n._T('scenes.shared.dialog.send-email.messages.email-limit-reached');
    }

    protected sameEmailsOnDocuments(documents: { clientEmails: string[]}[]): boolean {
        if (documents.length === 0) return true;

        const emailsToCompare = head(documents).clientEmails;
        return documents.every(({ clientEmails }) => clientEmails.length === emailsToCompare.length && sameValues(clientEmails, emailsToCompare));
    }

    public abstract sendEmailAsync(): Promise<void>;

    protected abstract onSendEmailSuccess(params: unknown, _: unknown): void;
}
